import TableFunction from '../../../../../../../components/table/TableFunction';
import { VIEW_TYPE } from '../../../../../../../enums/ViewType';
import React, { useEffect, useState } from 'react';
import { SEARCH_MODEL } from '../../../../../../../components/table/Model/TableModel';
import { SimpleSearch } from '../../../../../../../reduxToolkit/Search/SearchApi';
import DropDownMenuProfile from '../../../../../../../components/DropDown/DropDownMenuProfile';
import { permissionObj } from '../../../../../../../Helpers/permission';
import DropDownMenuItem from '../../../../../../../components/DropDown/DropDownMenuItem';
import routes from '../../../../../../../Routes/routes';
import {
    DeleteIconsOptions,
    DetailsIconsOptions,
    UpdateIconsOptions
} from '../../../../../../../components/buttons/IconButtons';
import { useDispatch, useSelector } from 'react-redux';
import { errorMsg } from '../../../../../../../Apis/functions';
import { ATTENDANCE_SUBVIEW_MODEL } from '../../../Model/Model';
import { DeleteItemsApi } from '../../../../../../../reduxToolkit/Commons/CommonsApi';
import {useNavigate} from 'react-router-dom';

/**
 * @Author: Cecilia López Martínez
 * @JobTitle : Full Stack Developer - IBL
 * @Email : clopez@ibl.mx
 *
 * List hook for the Shift Access table. Owns the paginated search of shifts,
 * row selection / delete buffer, the row options menu (edit / delete) and
 * registers the bulk-delete action with the parent manage view.
 *
 * @param {Object} props
 * @param {Function} props.registerAction - Registers the section's bulk-delete handler with the parent view.
 * @param {string} props.option - Table/model name used by search and delete.
 * @param {Array} props.selectForDelete - Currently selected row ids.
 * @param {Function} props.setSelectForDelete - Selection setter.
 * @param {string} props.searchElement - Current search input value.
 * @param {string} [props.view] - Table view type (list/grid).
 * @returns {Object} Table state, handlers and render helpers.
 */
const ShiftAccessApp = (
    {
        registerAction,
        option,
        selectForDelete,
        setSelectForDelete,
        searchElement,
        view = VIEW_TYPE.LIST.name,
    }
) => {

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const [searchResults, setSearchResults] = useState(null);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [modified, setModified] = useState(false);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const searchId = 'txt_saa_search_shifts';
    const canUpdate = DEV || permission?.includes(permissionObj?.WEB_ATTENDANCE_SHIFT_UPDATE);
    const canDelete = DEV || permission?.includes(permissionObj?.WEB_ATTENDANCE_SHIFT_DELETE);
    const canRead = DEV || permission?.includes(permissionObj?.WEB_ATTENDANCE_SHIFT_READ);
    // Without the update permission, read only users open the same view as details
    const canOpenDetails = !canUpdate && canRead;
    const showOptionsMenu = canUpdate || canDelete || canOpenDetails;

    const columns = [
        { field: 'name', label: 'schedule' }
    ];

    /**
     * Opens the delete confirmation modal.
     */
    const handleDelete = () => {
        setDeleteModalShow(true);
    };

    /**
     * Deletes the selected shifts, then refreshes the list and clears the
     * selection on success.
     */
    const fnDelete = () => {
        if (selectForDelete.length > 0) {
            dispatch(DeleteItemsApi({ tableName: option, body: selectForDelete })).then(({ payload: { data: { success, code } } }) => {
                if (success === true) {
                    setModified(true);
                    setSelectForDelete([]);
                } else errorMsg(code);
            });
        }
    };

    useEffect(() => {
        registerAction(
            ATTENDANCE_SUBVIEW_MODEL.MANAGE.SHIFT_ACCESS,
            { handleDelete }
        );
    }, [selectForDelete]);

    /**
     * Row options menu (edit / delete), each entry gated by its permission.
     * @param {Object} row - Shift row.
     * @returns {JSX.Element}
     */
    const RenderOptionsMenu = (row) => {
        return (
            <>
                <DropDownMenuProfile
                    openMenu={openMenu}
                    handleMenuToggle={handleMenuToggle}
                    menuId={`menu_saa_${row.id}`}
                    isAccordion={true}
                >
                    {canUpdate &&
                        <DropDownMenuItem
                            id={`btn_saa_edit_${row.id}`}
                            index={row.id}
                            handleClose={handleClose}
                            name={t('update')}
                            url={`${routes.attendanceShiftAccess.edit}${row.id}`}
                            icon={<UpdateIconsOptions />}
                        />
                    }

                    {canOpenDetails &&
                        <DropDownMenuItem
                            id={`btn_saa_details_${row.id}`}
                            index={row.id}
                            handleClose={handleClose}
                            name={t('details')}
                            url={`${routes.attendanceShiftAccess.edit}${row.id}`}
                            icon={<DetailsIconsOptions />}
                        />
                    }

                    {canDelete &&
                        <DropDownMenuItem
                            id={`btn_saa_delete_${row.id}`}
                            index={row.id}
                            handleClose={handleClose}
                            name={t('delete')}
                            fontColor='font-color-error-600'
                            iconColor={"icon-color-error-code-600"}
                            click={() => {
                                setSelectForDelete([row.id])
                                handleDelete();
                            }}
                            icon={<DeleteIconsOptions />}
                        />
                    }

                </DropDownMenuProfile>
            </>
        )
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = page => {
        const body = SEARCH_MODEL.setObject({ page: page, size: rowsPerPage, model: option, key: searchElement, view: view });
        dispatch(SimpleSearch({ body })).then(({ payload: { data: { data, success } } }) => {
                if (success === true) {
                    setSearchResults(data);
                }
            }
        );
    }

    const { t, handleCheckboxChange, handleDeleteAll,
        pagination, page, rowsPerPage,
        handleRowExpand,
        handleMenuToggle,
        handleClose,
        openMenu
    } = TableFunction({
        selectedList: selectForDelete,
        setSelectedList: setSelectForDelete,
        data: searchResults,
        view: view,
        labelRowsPerPage: 'shifts_per_page',
        fixedPagination: false,
        hasContainer: true,
        setIsAllChecked,
        searchElement, //New
        getData,//New
        modified,//New
        setModified//New
    });

    return {
        t,
        DEV, permission,navigate,
        searchId,
        deleteModalShow, setDeleteModalShow,
        searchResults, setSearchResults,
        page, rowsPerPage,
        handleRowExpand,
        handleCheckboxChange,
        handleDeleteAll,
        pagination,
        columns,
        RenderOptionsMenu,
        showOptionsMenu,
        canDelete,
        fnDelete
    }
}

export default ShiftAccessApp
